'use strict';

var cssUtils = require('../core/cssUtils');
var fragment = require('../core/fragment');
var _ = require('lodash');
var CONSTANTS = require('./constants');

var GOOGLE_FONT_SERVICE_URL = '//fonts.googleapis.com/css?family=';

var UPLOADED_FONT_MASK = 'wfont_[0-9a-f]{6}_[0-9a-f]{32}';
var SHORTY_FONT_MASK = 'wf_[0-9a-f]{25}';
var UPLOADED_FONT_TEST = new RegExp('^' + UPLOADED_FONT_MASK);
var UPLOADED_FONT_MATCH = new RegExp(UPLOADED_FONT_MASK + '|' + SHORTY_FONT_MASK, 'g');

var createFontUtils = function createFontUtils(fontsMetadata) {
    var parseFontStr = cssUtils.parseFontStr;

    function getFontsUrlWithParams(fontNamesObject, documentType, characterSets) {
        var fontsFamiliesArray = _.isArray(fontNamesObject) ? fontNamesObject : _.keys(fontNamesObject);
        var query = getFontsQuery(fontsFamiliesArray, documentType, characterSets);
        if (query) {
            return GOOGLE_FONT_SERVICE_URL + query;
        }

        return '';
    }

    function getFontsQuery(fontsFamiliesArray, documentType, characterSets) {
        var fontQuery = '';
        var permissions = getFontsPermissions(documentType);
        _.forEach(fontsFamiliesArray, function (fontFamily) {
            var font = fontsMetadata[fontFamily];
            if (font && font.cdnName && _.includes(permissions, font.permissions)) {
                fontQuery += font.cdnName + ':n,b,i,bi|';
            }
        });
        if (fontQuery === '') {
            return null;
        }
        if (characterSets) {
            fontQuery += '&subset=' + characterSets.join(',');
        }
        return fontQuery;
    }

    function getFontsPermissions(documentType) {
        var permissions = ['all', 'legacy'];
        if (documentType === 'WixSite') {
            permissions.push('studio');
        }
        return permissions;
    }

    function getWixStoredFontsCssUrlsWithParams(baseUrl, characterSets, altBaseUrl) {
        if (altBaseUrl && /localhost|127.0.0.\d/.test(baseUrl)) {
            baseUrl = altBaseUrl;
        }
        baseUrl = baseUrl.replace(/^http:/, '');
        var fontsCssBaseUrl = baseUrl.replace(/\/$/, '') + '/static/css/user-site-fonts/';
        return _.map(characterSets, function (cs) {
            return fontsCssBaseUrl + cs + '.css';
        });
    }

    function parseStyleFont(fontStyleName, themeFonts, themeColors) {
        if (themeFonts[fontStyleName]) {
            var fontObject = parseStringFont(themeFonts[fontStyleName]);
            return parseThemeFontColor(fontObject, themeColors);
        }
        return parseStringFont(fontStyleName);
    }

    function parseStringFont(fontValue) {
        var fontObject = parseFontStr(fontValue);
        fontObject.fontWithFallbacks = getFontFamilyWithFallbacks(fontObject.family);
        return fontObject;
    }

    function getFontFamilyWithFallbacks(fontName) {
        var font = fontsMetadata[fontName.toLowerCase()];
        var fontFamily = font && font.fontFamily;
        var fallbacks = void 0;

        if (font) {
            fallbacks = fontFamily;
            if (font.fallbacks !== '') {
                fallbacks += ',' + font.fallbacks;
            }
            fallbacks += ',' + font.genericFamily;
        } else {
            fallbacks = fontName;
        }

        return formatFallbackList(fallbacks);
    }

    function formatFallbackList(fallbacks) {
        //surround fonts with quotes if font name contains spaces or non-latin chars
        return _(fallbacks).split(',').invokeMap('replace', /.*[^\w\d\-].*/, '"$&"').join(',');
    }

    function parseThemeFontColor(fontObject, themeColors) {
        var fontColor = fontObject.color && fontObject.color.match(/{([^}]+)}/);
        if (themeColors && fontColor && themeColors[fontColor[1]]) {
            fontObject.cssColor = themeColors[fontColor[1]];
        } else {
            fontObject.cssColor = fontObject.color;
        }
        return fontObject;
    }

    function getFontFamily(fontStr) {
        return fontStr.split(' ')[4];
    }

    function fontToCSSWithColor(font, themeColors) {
        var fontVal = getFontVal(font);
        var fontCss = getFontCSSFromFontString(fontVal);
        var colorCss = getColorCSSFromFontString(fontVal, themeColors);

        return fontCss + colorCss;
    }

    /**
     * @param themeFonts: {font_0: 'fontString', font_1: 'fontString'}
     * @param themeColors
     * @returns ".font_0: {"font: 'normal normal normal 45px/1.4em Open+Sans; color: #123456;} "
     */
    function getThemeFontsCss(themeFonts, themeColors) {
        var result = '';

        _.forEach(themeFonts, function (fontString, fontIndex) {
            result += '.font_' + fontIndex + ' {font: ' + fontToCSSWithColor(fontString, themeColors) + '} \n';
        });

        return result;
    }

    function getFontVal(fontString, themeData) {
        if (_.startsWith(fontString, 'font_')) {
            var fontParts = fontString.split('font_');
            if (fontParts.length === 2) {
                return themeData.font[fontParts[1]];
            }
        }
        return fontString;
    }

    function getFontsByPermissionToFontsList(documentType) {
        var permissions = getFontsPermissions(documentType);
        var langs = _.reduce(fontsMetadata, function (res, value, key) {
            var fontCharacterSets = value.characterSets;
            if (_.includes(permissions, value.permissions)) {
                value.cssFontFamily = getFontFamilyWithFallbacks(key);
                _.forEach(fontCharacterSets, function (charSet) {
                    if (!res[charSet]) {
                        res[charSet] = [];
                    }
                    res[charSet].push(value);
                });
            }
            return res;
        }, {});

        _.forOwn(langs, function (fontList, charSet) {
            langs[charSet] = _.sortBy(fontList, ['displayName']);
        });
        return langs;
    }

    function getCurrentSelectablefontsWithParams(documentType, characterSets) {
        var languagesFontsLists = getFontsByPermissionToFontsList(documentType);
        return _(CONSTANTS.POSSIBLE_CHARACTERS_SETS).intersection(characterSets).map(function (lang) {
            return {
                lang: lang,
                fonts: languagesFontsLists[lang]
            };
        }).value();
    }

    function getFontFallback(fontFamily) {
        var cleanFontName = fontFamily.replace(/\+/g, ' ').toLowerCase();
        var fontMeta = fontsMetadata[cleanFontName];
        if (fontMeta) {
            var fallback = fontMeta.fallbacks;
            if (fallback) {
                fallback += ',';
            }
            fallback += fontMeta.genericFamily;
            return fallback;
        }

        return '';
    }

    function getFontCSSFromFontString(fontVal) {
        var font = fontVal;
        if (_.includes(font, '#')) {
            font = font.slice(0, font.indexOf('#'));
        }
        font = font.replace(/\{color_\d+\}/, '');
        var fontFamily = getFontFamily(font);
        var fullFontFamily = getFullFontFamily(fontFamily);
        var childFont = font.replace(fontFamily, fullFontFamily);
        return childFont + ';';
    }

    function getFullFontFamily(fontFamily) {
        var fullFontFamily = fontFamily;
        var fallback = getFontFallback(fontFamily);
        if (fallback) {
            fullFontFamily = fullFontFamily + ',' + fallback;
        }

        //surround fonts with quotes if font name contains spaces or non-latin chars
        fullFontFamily = fullFontFamily.replace(/[^,]*[^\w,\d\-][^,]*/g, function (fontFamilyStr) {
            return '\'' + fontFamilyStr.replace(/\+/g, ' ') + '\'';
        });
        return fullFontFamily;
    }

    function getColorCSSFromFontString(fullFontString, themeColors) {
        var colorParts = fullFontString.match(/{color_(\d+)}/);
        if (!colorParts) {
            var colorFromFontString = fullFontString.match(/#[A-Z0-9]{3,6}/);
            if (colorFromFontString) {
                return 'color:' + colorFromFontString[0] + ';';
            }
            return '';
        }
        var colorIndexInTheme = colorParts[1];
        var colorFromTheme = themeColors[colorIndexInTheme];
        if (colorFromTheme.indexOf('#') === 0) {
            return 'color:' + colorFromTheme + ';';
        }
        return 'color:rgba(' + colorFromTheme + ');';
    }

    function getFontFamilyPermissions(fontFamily) {
        var fontDef = _.find(fontsMetadata, { fontFamily: fontFamily });
        return fontDef && fontDef.permissions;
    }

    function getFontsMetaData() {
        return fontsMetadata;
    }

    function collectFontsFromTextDataArray(textHtml) {
        var _ref = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {},
            _ref$onlyUploaded = _ref.onlyUploaded,
            onlyUploaded = _ref$onlyUploaded === undefined ? false : _ref$onlyUploaded,
            _ref$useRegExp = _ref.useRegExp,
            useRegExp = _ref$useRegExp === undefined ? false : _ref$useRegExp;

        if (!textHtml) {
            return [];
        }

        if (onlyUploaded) {
            return textHtml.match(UPLOADED_FONT_MATCH);
        }

        if (useRegExp) {
            var fontFamilies = textHtml.match(/\Wfont-family\s*:[^;":]+/g);
            return _(fontFamilies).invokeMap('replace', /.+:(.*)/, '$1').invokeMap('trim').compact().value();
        }

        var doc = new fragment.DOMParser().parseFromString(textHtml, 'text/html');
        return _(doc.querySelectorAll('*')).toArray().map(function (el) {
            return _.get(el, ['style', 'font-family']);
        }).compact().invokeMap('trim').value();
    }

    function isUploadedFontFamily(fontFamilyStr) {
        if (_.isNil(fontFamilyStr) || _.isEmpty(fontFamilyStr)) {
            return false;
        }

        var fontName = _.head(fontFamilyStr.split(','));
        return UPLOADED_FONT_TEST.test(fontName);
    }

    function getUploadedId(uploadedFonStr) {
        return isUploadedFontFamily(uploadedFonStr) ? uploadedFonStr.replace(CONSTANTS.LONG_UPLOADED_FONT_PREFIX, '').trim() : null;
    }

    function getUploadedFontFaceStyles(fontFamiliesArr, mediaRootUrl) {
        var fixedMediaRootUrl = fixMediaRootUrl(mediaRootUrl);
        return _(fontFamiliesArr).filter(isUploadedFontFamily).reduce(function (accm, font) {
            return accm + getUploadFontFace(font, fixedMediaRootUrl);
        }, '');
    }

    function fixMediaRootUrl(mediaRootUrl) {
        return _.startsWith(mediaRootUrl, 'http://') ? mediaRootUrl.replace('http://', 'https://') : mediaRootUrl;
    }

    function getUploadFontFace(font, mediaRootUrl) {
        var fontName = _.head(font.split(','));
        var fontId = getUploadedId(fontName);
        var shortFontName = getShortUploadedFontFamily(fontName);
        var fontFaceString = '@font-face {\nfont-family: ' + shortFontName + ';\nsrc: url("' + mediaRootUrl + 'ufonts/' + fontId + '/woff/file.woff") format("woff"),\nurl("' + mediaRootUrl + 'ufonts/' + fontId + '/woff2/file.woff2") format("woff2"),\nurl("' + mediaRootUrl + 'ufonts/' + fontId + '/ttf/file.ttf") format("ttf");\n}\n';
        return fontFaceString;
    }

    function getShortUploadedFontFamily(font) {
        return 'wf_' + /^wfont_[0-9a-f]{6}_([0-9a-f]{25})[0-9a-f]{7}/.exec(font)[1];
    }

    function getMetadata(fontNames) {
        return _.compact(_.map(fontNames, function (fontName) {
            return getFontsMetaData()[fontName];
        }));
    }

    function getFontFamilyByStyleId(generalThemeData, stylesId) {
        var fontStyles = generalThemeData.font;
        var fontIndex = parseInt(stylesId.substring(stylesId.indexOf('_') + 1), 10);
        var fontStyleString = fontStyles[fontIndex];
        var fontFamily = '';
        if (fontStyleString) {
            fontFamily = parseFontStr(fontStyleString).family.toLowerCase();
        }
        return fontFamily;
    }

    function getUploadedFontValue(fontId) {
        var fontName = '' + (CONSTANTS.LONG_UPLOADED_FONT_PREFIX + fontId);
        var fontShortName = getShortUploadedFontFamily(fontName);
        return [fontName, fontShortName].join(',');
    }

    return {
        parseFontStr: parseFontStr,
        getFontsUrlWithParams: getFontsUrlWithParams,
        getWixStoredFontsCssUrlsWithParams: getWixStoredFontsCssUrlsWithParams,
        parseStyleFont: parseStyleFont,
        getFontFamilyWithFallbacks: getFontFamilyWithFallbacks,
        getFontFamily: getFontFamily,
        getCurrentSelectablefontsWithParams: getCurrentSelectablefontsWithParams,
        fontToCSSWithColor: fontToCSSWithColor,
        getThemeFontsCss: getThemeFontsCss,
        getFontFallback: getFontFallback,
        getFontFamilyPermissions: getFontFamilyPermissions,
        getFontsMetaData: getFontsMetaData,
        collectFontsFromTextDataArray: collectFontsFromTextDataArray,
        isUploadedFontFamily: isUploadedFontFamily,
        getUploadedId: getUploadedId,
        getUploadedFontFaceStyles: getUploadedFontFaceStyles,
        getMetadata: getMetadata,
        getFontFamilyByStyleId: getFontFamilyByStyleId,
        getUploadedFontValue: getUploadedFontValue,
        getShortUploadedFontFamily: getShortUploadedFontFamily
    };
};

module.exports = createFontUtils;
//# sourceMappingURL=fontUtils.js.map